"use client"

import * as React from "react"
import { Button } from "@components/ui/button"
import { Calendar } from "@components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover"
import { Card, CardContent, CardFooter } from "@components/ui/card"
import type { TSelectDateRangeProps } from "./types"
import { defaultDateRangePresets } from "./presets"
import { Badge } from "@components/ui/badge"
import { Separator } from "@components/ui/separator"
import { tailwindBreakpoints } from "@hooks/useMediaQuery"
import { useSelectDateRange } from "./utils"
import { cn } from "@components/ui/lib/utils"

export function SelectDateRange({
  value,
  onChange,
  placeholder = "Select date range",
  presets = defaultDateRangePresets,
  disabled = false,
  icon,
  className,
}: TSelectDateRangeProps) {
  const {
    currentRange,
    handleSelect,
    handlePresetClick,
    handleClear,
    activePreset,
    displayText,
  } = useSelectDateRange({ value, onChange, presets })

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "tw:flex tw:w-full tw:justify-start tw:gap-2 tw:border-border",
            className,
          )}
        >
          {icon && React.createElement(icon)}
          {placeholder}
          {displayText && (
            <>
              <Separator orientation="vertical" />
              <Badge
                variant="secondary"
                className="tw:rounded-sm tw:px-1 tw:font-normal"
              >
                {displayText}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="tw:w-auto tw:max-w-min tw:border-0 tw:p-0"
        align="end"
        side="bottom"
      >
        <Card>
          <CardContent>
            <Calendar
              mode="range"
              selected={currentRange}
              captionLayout="dropdown"
              numberOfMonths={tailwindBreakpoints.md() ? 2 : 1}
              onSelect={(range) => handleSelect(range)}
              className="tw:rounded-md"
              required
            />
          </CardContent>
          <CardFooter className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:pt-4">
            <div className="tw:flex tw:w-full tw:flex-wrap tw:gap-2">
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant={
                    activePreset?.label === preset.label ? "default" : "outline"
                  }
                  size="sm"
                  className="tw:flex-1"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="tw:w-full"
              onClick={handleClear}
            >
              Clear
            </Button>
          </CardFooter>
        </Card>
      </PopoverContent>
    </Popover>
  )
}
